import React from 'react';
import { Target, Palette, TrendingUp, Award, Package, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { whyChooseUs } from '../data/mock';

export const WhyChooseUs = () => {
  const iconMap = {
    Target,
    Palette,
    TrendingUp,
    Award,
    Package,
    CheckCircle2
  };

  return (
    <section id="why-us" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Why Choose <span className="text-blue-600">IN SMART MEDIA</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Your trusted partner for complete advertising and branding solutions
          </p>
        </div>

        {/* Success Image - Real Service Examples */}
        <div className="mb-16 grid md:grid-cols-3 gap-8 items-center">
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_insmart-media/artifacts/ixqjh1jq_Local%20Tv%20Channel.jpeg"
              alt="Local TV Channel Advertising"
              width="400"
              height="300"
              loading="lazy"
              decoding="async"
              className="rounded-2xl shadow-xl w-full h-auto object-cover"
            />
          </div>
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_insmart-media/artifacts/n0n7zpy3_Google%20Photography.jpeg"
              alt="Google Business Photography"
              width="400"
              height="300"
              loading="lazy"
              decoding="async"
              className="rounded-2xl shadow-xl w-full h-auto object-cover"
            />
          </div>
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_insmart-media/artifacts/jr1ipvbn_Mobile%20vhan.jpeg"
              alt="Mobile Van Advertising"
              width="400"
              height="300"
              loading="lazy"
              decoding="async"
              className="rounded-2xl shadow-xl w-full h-auto object-cover"
            />
          </div>
        </div>

        {/* Why Choose Us Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {whyChooseUs.map((item, index) => {
            const Icon = iconMap[item.icon];
            return (
              <Card 
                key={index}
                className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-blue-600 hover:scale-105"
              >
                <CardContent className="p-8 text-center">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-600 transition-colors">
                    {Icon && <Icon className="text-blue-600 group-hover:text-white transition-colors" size={32} />}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Value Proposition Section */}
        <div className="mt-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 md:p-12 text-white">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-4">Complete Offline + Digital Solutions</h3>
              <p className="text-blue-100 text-lg leading-relaxed mb-6">
                We deliver comprehensive marketing solutions under one roof, combining traditional and digital advertising for maximum impact and reach.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <CheckCircle2 size={24} className="flex-shrink-0" />
                  <span>Data-driven strategies for right audience targeting</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 size={24} className="flex-shrink-0" />
                  <span>Creative excellence with perfect messaging</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 size={24} className="flex-shrink-0" />
                  <span>Real-time performance tracking</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 size={24} className="flex-shrink-0" />
                  <span>Low-budget, high-impact campaigns</span>
                </li>
              </ul>
            </div>
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_insmart-media/artifacts/ttltqjbu_In%20smart%20Media%20New%20Logo.jpeg"
                alt="IN SMART MEDIA - SMART Marketing Strategy"
                width="600"
                height="400"
                loading="lazy"
                decoding="async"
                className="rounded-xl shadow-2xl w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div className="p-6 bg-blue-50 rounded-xl">
            <div className="text-4xl font-bold text-blue-600 mb-2">17+</div>
            <div className="text-gray-700 font-medium">Years of Excellence</div>
          </div>
          <div className="p-6 bg-blue-50 rounded-xl">
            <div className="text-4xl font-bold text-blue-600 mb-2">500+</div>
            <div className="text-gray-700 font-medium">Successful Campaigns</div>
          </div>
          <div className="p-6 bg-blue-50 rounded-xl">
            <div className="text-4xl font-bold text-blue-600 mb-2">50+</div>
            <div className="text-gray-700 font-medium">Advertising Services</div>
          </div>
          <div className="p-6 bg-blue-50 rounded-xl">
            <div className="text-4xl font-bold text-blue-600 mb-2">23+</div>
            <div className="text-gray-700 font-medium">Founder's Experience</div>
          </div>
        </div>
      </div>
    </section>
  );
};
